import { useQuery } from '@tanstack/react-query';
import { getDashboardDependencies } from '@api/dashboard';

/**
 * Custom hook to fetch report dependencies (saved reports and scheduled reports)
 * Uses react-query for caching and automatic refetching
 *
 * @param {number|string|null} dashboardId - The dashboard/report ID to check dependencies for
 * @param {Object} options - Additional options
 * @param {boolean} options.enabled - Whether to enable the query (default: true when dashboardId is provided)
 * @returns {Object} - Query result with dependencies data
 */
export function useReportDependencies(dashboardId, options = {}) {
  const { enabled = true } = options;

  const query = useQuery({
    queryKey: ['reportDependencies', dashboardId],
    queryFn: async () => {
      if (!dashboardId) {
        return {
          dashboardId: null,
          savedReportsCount: 0,
          scheduledReportsCount: 0,
          savedReports: [],
          scheduledReports: [],
        };
      }

      const result = await getDashboardDependencies(dashboardId);

      if (result.status === 'success') {
        return result.data;
      }

      // Return empty data on error instead of throwing
      return {
        dashboardId,
        savedReportsCount: 0,
        scheduledReportsCount: 0,
        savedReports: [],
        scheduledReports: [],
      };
    },
    enabled: enabled && !!dashboardId,
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: false,
  });

  // Derived values for convenience
  const hasDependencies = (query.data?.savedReportsCount || 0) > 0
    || (query.data?.scheduledReportsCount || 0) > 0;

  return {
    ...query,
    dependencies: query.data,
    hasDependencies,
    savedReportsCount: query.data?.savedReportsCount || 0,
    scheduledReportsCount: query.data?.scheduledReportsCount || 0,
    savedReports: query.data?.savedReports || [],
    scheduledReports: query.data?.scheduledReports || [],
  };
}

export default useReportDependencies;


